import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    Button,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { useCreatePlantMutation } from '../../../../api/services/plantApi';
import BackdropLoader from '../../../../components/common/BackdropLoader';
import Swal from 'sweetalert2';
import PlantForm, { type PlantFormData } from '../PlantForm';
import { plantValidationSchema, zodResolver } from '../../plantParser';

const PlantCreate: React.FC = () => {
    const navigate = useNavigate();
    const [createPlant, { isLoading }] = useCreatePlantMutation();
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const {
        control,
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors
    } = useForm<PlantFormData>({
        mode: 'onBlur',
        resolver: zodResolver(plantValidationSchema),
        defaultValues: {
            name: '',
            description: '',
            displayName:'',
            autoClosureTime: '',
            shifts: [{ start: '', end: '' }],
            lines: '',
            timezone:''
        },
    });


    const isFormValid = () => {
        const hasErrors = Object.keys(errors).length > 0 || Object.keys(formErrors).length > 0;
        return !hasErrors;
    };

    const onFormSubmit = async (data: PlantFormData) => {
        // Clear previous errors
        setFormErrors({});
        clearErrors();

        // Validate using safeParse
        const validationResult = plantValidationSchema.safeParse(data);

        if (!validationResult.success) {
            // Handle validation errors
            const fieldErrors: Record<string, string> = {};

            validationResult.error.issues.forEach((issue: any) => {
                const path = issue.path.join('.');
                fieldErrors[path] = issue.message;

                // Set errors for react-hook-form
                if (issue.path.length === 1) {
                    setError(issue.path[0] as keyof PlantFormData, {
                        type: 'manual',
                        message: issue.message
                    });
                } else if (issue.path.length === 2) {
                    // For array fields like shifts[0].start
                    const fieldName = `${issue.path[0]}.${issue.path[1]}` as any;
                    setError(fieldName, {
                        type: 'manual',
                        message: issue.message
                    });
                } else if (issue.path.length === 3) {
                    // For nested array fields like shifts[0].start
                    const fieldName = `${issue.path[0]}.${issue.path[1]}.${issue.path[2]}` as any;
                    setError(fieldName, {
                        type: 'manual',
                        message: issue.message
                    });
                }
            });

            setFormErrors(fieldErrors);

            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fix the validation errors before submitting.',
                confirmButtonColor: '#003EB0',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            const apiPayload = {
                PLANTNAME: data.name,
                PLANTDESC: data.description,
                MICRO_STOPPAGE_TIME: `00:${data.autoClosureTime}`,
                lineCount: parseInt(data.lines),
                PLANTDISPLAYNAME:data.displayName,
                TIMEZONE:data.timezone,
                shifts: data.shifts.map((shift, index) => ({
                    SHIFTNAME: String(index + 1),
                    SHIFTDESC: `Shift ${index + 1}`,
                    STARTTIME: shift.start,
                    ENDTIME: shift.end,
                    PLANTNAME: data.name
                }))
            };

            await createPlant(apiPayload).unwrap();

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Plant created successfully',
                confirmButtonColor: '#003EB0',
                confirmButtonText: 'OK'
            }).then(() => {
                navigate('/plant');
            });

        } catch (err: any) {
            console.log(err, 'errerrerr');

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: err?.data?.message || 'Failed to create plant. Please try again.',
                confirmButtonColor: '#003EB0',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleCancel = () => {
        navigate('/plant');
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container sx={{ width: '100%' }}>
                <BackdropLoader openStates={isLoading} />
                <Grid size={{ xs: 6 }} sx={{ width: "50%" }}>
                    <Grid container sx={{ width: '100%' }}>
                        <Grid size={{ xs: 'auto' }}>
                            <ArrowBackIcon sx={{ mr: 1, color: '#1976d2' }} onClick={handleCancel} />
                        </Grid>
                        <Grid size={{ xs: 'auto' }}>
                            <Typography variant="h6" sx={{
                                color: '#172B4D', fontWeight: 500,
                                fontSize: { xs: '16px', sm: '20px', md: '24px' },
                                lineHeight: { xs: 1.4, sm: 1.5 },
                            }}>
                                New Plant Creation
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ width: '50%', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        onClick={handleCancel}
                        variant="outlined"
                        disabled={isLoading}
                        sx={{
                            textTransform: 'none',
                            color: '#172B4D',
                            border: '1px solid rgba(9, 30, 66, 0.31)',
                            borderColor: '#ddd',
                            background: "rgba(9, 30, 66, 0.06)",
                            borderRadius: '6px',
                            mr: 1,
                            '&:hover': {
                                borderColor: '#bbb',
                                bgcolor: '#f5f5f5'
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValid() || isLoading}
                        onClick={handleSubmit(onFormSubmit)}
                        sx={{
                            borderRadius: '6px',
                            textTransform: 'none',
                            background: '#003EB0',
                            '&:hover': {
                                bgcolor: '#1565c0'
                            },
                        }}
                    >
                        {isLoading ? 'Creating...' : 'Create Plant'}
                    </Button>
                </Grid>
            </Grid>

            <Box sx={{ p: 4, background: '#FFF', mt: 1 }}>
                <form>
                    <PlantForm
                        control={control}
                        errors={errors}
                        isLoading={isLoading}
                        formErrors={formErrors}
                    />
                </form>
            </Box>
        </Box>
    );
};

export default PlantCreate;